import { isEqual } from "@common/helpers/comparison";
import type { View } from "@common/types/views/views";
import { DEFAULT_VIEW_ID } from "@components/views/constants";
import {
    ASSIGNED_TO_ME_VIEW_ID,
    CLIENT_REQUESTS_VIEW_ID,
    MONTH_END_CLOSE_VIEW_ID,
    UNASSIGNED_VIEW_ID,
    WAITING_ON_CLIENT_VIEW_ID,
} from "./OrganizationTaskViews";

/** What an opinionated view shows when it has no tasks at all. */
export interface OpinionatedViewEmptyState {
    header: string;
    /** The view the button switches to; null shows no button. */
    action: { label: string; viewId: string } | null;
}

const HEADER_BY_VIEW_ID: ReadonlyMap<string, string> = new Map([
    [ASSIGNED_TO_ME_VIEW_ID, "No tasks assigned to you"],
    [UNASSIGNED_VIEW_ID, "No unassigned tasks"],
    [WAITING_ON_CLIENT_VIEW_ID, "Nothing waiting on clients"],
    [CLIENT_REQUESTS_VIEW_ID, "No client requests"],
    [MONTH_END_CLOSE_VIEW_ID, "No open month-end close tasks"],
]);

/** Assigned to me's button, in order of preference. */
const ASSIGNED_TO_ME_ACTIONS = [
    { label: "View unassigned", viewId: UNASSIGNED_VIEW_ID },
    { label: "View all tasks", viewId: DEFAULT_VIEW_ID },
];

/**
 * The whole-view empty state for `viewId`, or null for All tasks and saved
 * views. Assigned to me points at the first of Unassigned and All tasks
 * among the page's built-in views.
 */
export const getOpinionatedViewEmptyState = ({
    viewId,
    builtInViewIds,
}: {
    viewId: string;
    builtInViewIds: readonly string[];
}): OpinionatedViewEmptyState | null => {
    const header = HEADER_BY_VIEW_ID.get(viewId);
    if (header === undefined) {
        return null;
    }
    const action =
        viewId === ASSIGNED_TO_ME_VIEW_ID
            ? (ASSIGNED_TO_ME_ACTIONS.find((candidate) =>
                  builtInViewIds.includes(candidate.viewId),
              ) ?? null)
            : null;
    return { header, action };
};

/** Stored with a view's filters, but orders its tasks rather than narrowing them. */
const NON_NARROWING_FILTER_KEYS: ReadonlySet<string> = new Set(["taskSort"]);

type ViewFilters = View["filters"];

/** The filters that narrow tasks, with unset values dropped and lists in a stable order. */
const toNarrowingFilters = (filters: ViewFilters) =>
    Object.fromEntries(
        Object.entries(filters)
            .filter(
                ([key, value]) =>
                    !NON_NARROWING_FILTER_KEYS.has(key) &&
                    value !== undefined &&
                    value !== null &&
                    !(Array.isArray(value) && value.length === 0),
            )
            .map(([key, value]) => [
                key,
                Array.isArray(value) ? [...value].sort() : value,
            ]),
    );

/**
 * Whether the user narrowed the picked view: a search, or filters other than
 * the ones the view stores. Sort and unset filters don't count.
 */
export const hasNarrowedTaskView = ({
    filters,
    viewFilters,
    search,
}: {
    /** The page's current filters, as its full view stores them. */
    filters: ViewFilters;
    /** The picked view's own filters. */
    viewFilters: ViewFilters;
    search: string | undefined;
}): boolean =>
    Boolean(search) ||
    !isEqual(toNarrowingFilters(filters), toNarrowingFilters(viewFilters));
